import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { DateField } from "@/components/DateField";
import { toast } from "sonner";
import {
  Gear, CircleNotch, FilePlus, CheckCircle, CalendarPlus, ArrowLeft, Buildings, SealCheck,
} from "@phosphor-icons/react";

export default function GovOps() {
  const [caps, setCaps] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [visits, setVisits] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ club_id: "", date: "", time: "10:30", venue_name: "", venue_address: "", logistics_instructions: "" });
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [c, v, cl] = await Promise.all([
        api.get("/me/capabilities"), api.get("/gov/visits"), api.get("/directory/clubs"),
      ]);
      setCaps(c.data); setVisits(v.data.visits); setClubs(cl.data.clubs);
      if (c.data.can_gov_config) {
        const r = await api.get("/gov/config/revisions");
        setRevisions(r.data.revisions);
      }
      if (cl.data.clubs.length) setForm((f) => ({ ...f, club_id: f.club_id || cl.data.clubs[0].club_id }));
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const createVisit = async () => {
    if (!form.club_id || !form.date) { toast.error("Pick a club and date"); return; }
    setBusy(true);
    // Build a UTC ISO from IST local date+time.
    const startUtc = new Date(`${form.date}T${form.time}:00+05:30`).toISOString();
    try {
      const r = await api.post("/gov/visits", {
        club_id: form.club_id, scheduled_start_utc: startUtc,
        venue_name: form.venue_name, venue_address: form.venue_address,
        logistics_instructions: form.logistics_instructions, display_timezone: "Asia/Kolkata",
      });
      toast.success("Visit scheduled");
      navigate(`/gov/visits/${r.data.gov_visit_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create visit"); }
    setBusy(false);
  };

  const createDraftConfig = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gov/config/draft");
      toast.success("Draft revision created");
      navigate(`/gov/config/${r.data.revision.config_revision_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setBusy(false); }
  };
  const publishConfig = async (id) => {
    setBusy(true);
    try { await api.post(`/gov/config/${id}/publish`); toast.success("Published"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground t-support"><CircleNotch size={18} className="animate-spin text-primary" weight="bold" /> loading…</div>;

  return (
    <div className="max-w-5xl" data-testid="gov-ops-page">
      <button onClick={() => navigate("/gov")} className="flex items-center gap-1.5 t-support text-muted-foreground hover:text-foreground mb-3"><ArrowLeft size={14} /> GOV overview</button>
      <div className="flex items-center gap-2 eyebrow mb-3"><Gear size={14} weight="fill" className="text-primary" /> District Operations</div>
      <h1 className="t-page-title">Plan &amp; manage visits</h1>

      {/* Annual configuration */}
      {caps?.can_gov_config && (
        <section className="mt-8" data-testid="config-section">
          <h2 className="t-section-title">Annual GOV configuration</h2>
          <div className="mt-3 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] divide-y divide-border">
            {revisions.map((r) => (
              <div key={r.config_revision_id} data-testid={`config-rev-${r.revision_number}`} className="flex items-center justify-between px-5 py-3">
                <div>
                  <span className="t-body font-semibold">Revision {r.revision_number}</span>
                  <span className="ml-3 text-xs uppercase tracking-wide px-2 py-0.5 border rounded border-border text-muted-foreground">{r.status}</span>
                  <p className="t-support text-muted-foreground mt-0.5">{r.requirements_template?.length || 0} requirements · {r.agenda_framework?.length || 0} agenda parts</p>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`edit-config-${r.revision_number}`} onClick={() => navigate(`/gov/config/${r.config_revision_id}`)}
                    className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-semibold rounded hover:bg-secondary">
                    {r.status === "draft" ? "Edit" : "View"}
                  </button>
                  {r.status === "draft" && (
                    <button data-testid={`publish-${r.revision_number}`} onClick={() => publishConfig(r.config_revision_id)} disabled={busy}
                      className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold rounded hover:bg-primary/90 active:scale-95 disabled:opacity-60"><SealCheck size={14} weight="bold" /> Publish</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button data-testid="new-draft-config" onClick={createDraftConfig} disabled={busy}
            className="mt-3 inline-flex items-center gap-2 border border-border px-4 py-2 t-support font-semibold rounded-md hover:bg-secondary transition-colors"><FilePlus size={15} weight="bold" /> New draft revision</button>
        </section>
      )}

      {/* Create visit */}
      {caps?.can_gov_schedule && (
        <section className="mt-10" data-testid="create-visit-section">
          <h2 className="t-section-title">Schedule a visit</h2>
          <div className="mt-3 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-5 grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="eyebrow">Club</span>
              <select data-testid="visit-club-select" value={form.club_id} onChange={(e) => setForm({ ...form, club_id: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 t-body bg-background border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none">
                {clubs.map((c) => <option key={c.club_id} value={c.club_id}>{c.club_name}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="eyebrow">Date (IST)</span>
                <DateField value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="visit-date" placeholder="Select date"
                  className="mt-1.5 flex w-full items-center gap-2 px-3 py-2.5 t-body bg-background border border-border rounded-md" /></label>
              <label className="block"><span className="eyebrow">Time</span>
                <input data-testid="visit-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2.5 t-body bg-background border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none" /></label>
            </div>
            <label className="block"><span className="eyebrow">Venue name</span>
              <input data-testid="visit-venue" value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 t-body bg-background border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none" /></label>
            <label className="block"><span className="eyebrow">Venue address</span>
              <input data-testid="visit-address" value={form.venue_address} onChange={(e) => setForm({ ...form, venue_address: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 t-body bg-background border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none" /></label>
            <label className="block sm:col-span-2"><span className="eyebrow">Club-visible logistics</span>
              <textarea data-testid="visit-logistics" rows={2} value={form.logistics_instructions} onChange={(e) => setForm({ ...form, logistics_instructions: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 t-body bg-background border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none" /></label>
          </div>
          <button data-testid="create-visit-button" onClick={createVisit} disabled={busy}
            className="mt-3 inline-flex items-center gap-2 px-7 py-3 t-support font-bold rounded-full active:scale-95 disabled:opacity-60"
            style={{ background: "#F7A81B", color: "#3A2A00" }}>
            {busy ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : <CalendarPlus size={16} weight="bold" />} Schedule visit</button>
        </section>
      )}

      {/* Existing visits */}
      <section className="mt-10">
        <h2 className="t-section-title">Scheduled visits</h2>
        <div className="mt-3 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] divide-y divide-border" data-testid="ops-visit-list">
          {visits.map((v) => (
            <button key={v.gov_visit_id} onClick={() => navigate(`/gov/visits/${v.gov_visit_id}`)} data-testid={`ops-visit-${v.gov_visit_id}`}
              className="w-full text-left flex items-center justify-between px-5 py-3 hover:bg-secondary transition-colors">
              <span className="flex items-center gap-2 t-body"><Buildings size={16} weight="duotone" className="text-primary" /> {v.club_name}</span>
              <span className="t-support text-muted-foreground">{v.display_date} · {v.status}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
